import ReservationSidebar from '../Commun/ReservationSidebar';
import ReservationHeader from '../Commun/ReservationHeader';
import { useEffect, useState } from 'react';
import { Property, ReserBusinessSource } from '../../Services/Service';
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { fetchBusinessMonthlyReport, fetchBusinessPropertyMonthlyReport } from '../../Redux/Slices/BusinessSourceReportSlice';
import * as XLSX from "xlsx";
import "./BusinessReportTheme.css";

const reportSelectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 38,
        borderColor: state.isFocused ? "#5b4cf6" : "#d7dff0",
        boxShadow: state.isFocused ? "0 0 0 3px rgba(91, 76, 246, 0.12)" : "none",
        borderRadius: 8,
        fontSize: 12,
        "&:hover": { borderColor: "#5b4cf6" }
    }),
    multiValue: (base) => ({
        ...base,
        backgroundColor: "#213145",
        borderRadius: 999,
        color: "#fff",
        paddingLeft: 4
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: "#fff",
        fontSize: 11,
        fontWeight: 600
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: "#fff",
        borderRadius: "0 999px 999px 0",
        ":hover": { backgroundColor: "#3b32c2", color: "#fff" }
    }),
    option: (base, state) => ({
        ...base,
        fontSize: 12,
        backgroundColor: state.isSelected ? "#213145" : state.isFocused ? "#f1f4ff" : "#fff",
        color: state.isSelected ? "#fff" : "#1d2540"
    }),
    placeholder: (base) => ({ ...base, color: "#7b849c", fontSize: 12 }),
    singleValue: (base) => ({ ...base, color: "#1d2540", fontSize: 12 })
};

export const BusinessMonthlyReport = () => {
    const dispatch = useDispatch();

    const [propertyList, setPropertyList] = useState([]);
    const [selectedProperty, setSelectedProperty] = useState([]);
    const [selectedYear, setSelectedYear] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState([]);
    const [sources, setSources] = useState([]);
    const [selectedSource, setSelectedSource] = useState([]);
    const [reportType, setReportType] = useState("source"); // default

    const loading = useSelector(state => state.businessSourceReport.loading);

    // 🔹 Fetch Sources
    useEffect(() => {
        const fetchBusinessSource = async () => {
            try {
                const data = await ReserBusinessSource(333333);
                if (Array.isArray(data)) setSources(data);
            } catch (error) {
                console.log(error);
            }
        };
        fetchBusinessSource();
    }, []);

    // 🔹 Default values
    useEffect(() => {
        fetchProperty();

        const now = new Date();

        setSelectedYear([{
            value: now.getFullYear(),
            label: now.getFullYear().toString()
        }]);

        setSelectedMonth([{
            value: now.getMonth() + 1,
            label: now.toLocaleString('default', { month: 'long' })
        }]);
    }, []);

    const fetchProperty = async () => {
        const data = await Property();
        setPropertyList(data);

        setSelectedProperty(
            data.map(p => ({
                value: p.PropertyID,
                label: p.PropertyName
            }))
        );
    };

    // 🔹 Options
    const propertyOptions = propertyList.map(p => ({
        value: p.PropertyID,
        label: p.PropertyName
    }));

    const yearOptions = Array.from({ length: 10 }, (_, i) => {
        const y = new Date().getFullYear() - i;
        return { value: y, label: y.toString() };
    });

    const monthOptions = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ].map((m, i) => ({ value: i + 1, label: m }));

    const sourceOptions = sources
        ?.filter(s => s.BONGroupCode && s.BONBusinessSource)
        .map(s => ({
            value: s.BONGroupCode,
            label: s.BONBusinessSource
        }));

    // 🔹 Handlers
    const handlePropertyChange = v => setSelectedProperty(v || []);
    const handleYearChange = v => setSelectedYear(v || []);
    const handleMonthChange = v => setSelectedMonth(v || []);
    const handleSourceChange = v => setSelectedSource(v || []);

    // 🔹 Generate PDF page
    const generateMultiMonthPage = (doc, data, selectedMonths, yearObj, today, isFirstPage, selectedProperty) => {

        const pageWidth = doc.internal.pageSize.getWidth();

        if (!isFirstPage) doc.addPage();

        // 🔹 Title
        doc.setFontSize(16);
        doc.text("Source Monthly Report", pageWidth / 2, 10, { align: "center" });

        doc.setFontSize(10);
        doc.text(`Generated: ${today}`, pageWidth - 10, 10, { align: "right" });

        // =========================
        // 🔹 PROPERTY BOX (FINAL FIXED)
        // =========================

        let propertyList = [];

        if (selectedProperty.length === propertyOptions.length) {
            propertyList = ["All Property"];
        } else {
            propertyList = selectedProperty.map(p => p.label);
        }

        const startX = 12;
        const boxY = 14;

        const label = "Property:";
        const labelWidth = doc.getTextWidth(label);

        const textStartX = startX + labelWidth + 6;

        const lineHeight = 5;

        // 🔥 SMART AUTO WIDTH WRAP (FINAL FIX)
        const rowsProp = [];
        let currentLine = "";
        let currentWidth = 0;

        const maxWidth = pageWidth - textStartX - 12; // safe margin

        propertyList.forEach((name, index) => {

            const isLast = index === propertyList.length - 1;

            // ✅ Equal spacing both sides
            const separator = isLast ? "" : "  |  ";
            const text = name + separator;

            const textWidth = doc.getTextWidth(text);

            if (currentWidth + textWidth <= maxWidth) {
                currentLine += text;
                currentWidth += textWidth;
            } else {
                rowsProp.push(currentLine.trim());
                currentLine = text;
                currentWidth = textWidth;
            }
        });

        if (currentLine) {
            rowsProp.push(currentLine.trim());
        }

        // ✅ Tight height (no gap)
        const boxHeight = (rowsProp.length * lineHeight) + 1.5;

        // 🔘 Border
        doc.setDrawColor(180, 180, 180);
        doc.setLineWidth(0.5);
        doc.rect(10, boxY, pageWidth - 20, boxHeight);

        // 🔹 Label
        doc.setFont(undefined, "bold");
        doc.setFontSize(10);
        doc.text("Property:", startX, boxY + 4);

        // 🔹 Values
        doc.setFont(undefined, "normal");

        let y = boxY + 4;

        rowsProp.forEach(line => {
            doc.text(line, textStartX, y);
            y += lineHeight;
        });

        const startY = y + 3;

        // =========================
        // 🔢 NUMBER FORMAT
        // =========================
        const formatNumber = (num) => {
            if (!num) return "0";
            return Number(num).toLocaleString("en-IN");
        };

        const months = selectedMonths.map(m => m.value).sort((a, b) => a - b);
        const isMoreThan6 = months.length > 6;

        // =========================
        // ✅ CASE 1: ≤ 6 MONTHS
        // =========================
        if (!isMoreThan6) {

            const monthLabels = months.map(m => {
                const d = new Date(yearObj.value, m - 1);
                return d.toLocaleString('default', { month: 'short', year: 'numeric' });
            });

            const headerRow1 = [
                { content: "Business Source", rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
                ...monthLabels.map(label => ({
                    content: label,
                    colSpan: 3,
                    styles: { halign: 'center' }
                }))
            ];

            const headerRow2 = months.flatMap(() => ([
                { content: "RM NTS", halign: 'center' },
                { content: "REV.", halign: 'center' },
                { content: "ARR", halign: 'center' }
            ]));

            const rows = data
                .filter(item => {
                    return months.some(m =>
                        Number(item[`OCC_${m}`] || 0) !== 0 ||
                        Number(item[`REV_${m}`] || 0) !== 0 ||
                        Number(item[`ARR_${m}`] || 0) !== 0
                    );
                })
                .map(item => [
                    item.BusinessSource || "",
                    ...months.flatMap(m => ([
                        formatNumber(item[`OCC_${m}`] || 0),
                        formatNumber(item[`REV_${m}`] || 0),
                        formatNumber(item[`ARR_${m}`] || 0)
                    ]))
                ]);

            const totalCols = 1 + (months.length * 3);
            const totalWidth = pageWidth - 20;

            const firstColWidth = 30;
            const otherColWidth = (totalWidth - firstColWidth) / (totalCols - 1);

            const columnStyles = {
                0: { cellWidth: firstColWidth, halign: 'left', valign: 'middle' }
            };

            for (let i = 1; i < totalCols; i++) {
                columnStyles[i] = { cellWidth: otherColWidth, halign: 'right' };
            }

            autoTable(doc, {
                startY,
                head: [headerRow1, headerRow2],
                body: rows,
                theme: 'grid',

                styles: {
                    fontSize: 7,
                    cellPadding: 1.5,
                    lineWidth: 0.2
                },

                headStyles: {
                    fillColor: [41, 128, 185],
                    textColor: 255
                },

                columnStyles,
                margin: { left: 10, right: 10 }
            });
        }

        // =========================
        // 🔥 CASE 2: > 6 MONTHS
        // =========================
        else {

            const monthLabels = months.map(m => {
                const d = new Date(yearObj.value, m - 1);
                return d.toLocaleString('default', { month: 'short', year: 'numeric' });
            });

            const headerRow = [
                "Business Source",
                "Type",
                ...monthLabels
            ];

            const rows = [];

            data.forEach(item => {

                rows.push([
                    {
                        content: item.BusinessSource || "",
                        rowSpan: 2,
                        styles: { valign: 'middle', halign: 'left' }
                    },
                    "RM NTS",
                    ...months.map(m => formatNumber(item[`OCC_${m}`] || 0))
                ]);

                rows.push([
                    "REV.",
                    ...months.map(m => formatNumber(item[`REV_${m}`] || 0))
                ]);

                rows.push([
                    "ARR",
                    ...months.map(m => formatNumber(item[`ARR_${m}`] || 0))
                ]);
            });

            const totalCols = 2 + months.length;
            const totalWidth = pageWidth - 20;

            const firstColWidth = 30;
            const secondColWidth = 15;
            const otherColWidth = (totalWidth - firstColWidth - secondColWidth) / months.length;

            const columnStyles = {
                0: { cellWidth: firstColWidth, halign: 'left', valign: 'middle' },
                1: { cellWidth: secondColWidth, halign: 'center' }
            };

            for (let i = 2; i < totalCols; i++) {
                columnStyles[i] = { cellWidth: otherColWidth, halign: 'right' };
            }

            autoTable(doc, {
                startY,
                head: [headerRow],
                body: rows,
                theme: 'grid',

                styles: {
                    fontSize: 7,
                    cellPadding: 1.5,
                    lineWidth: 0.2,
                    valign: 'middle'
                },

                headStyles: {
                    fillColor: [41, 128, 185],
                    textColor: 255
                },

                columnStyles,
                margin: { left: 10, right: 10 }
            });
        }
    };

    // 🔹 Generate Property PDF page
    const generateMultiMonthPageProperty = (
        doc,
        data,
        selectedMonths,
        yearObj,
        today,
        isFirstPage,
        SourceNames
    ) => {

        const pageWidth = doc.internal.pageSize.getWidth();

        if (!isFirstPage) {
            doc.addPage();
        }

        // 🔹 Title
        doc.setFontSize(16);
        doc.text("Property Source Monthly Report", pageWidth / 2, 10, { align: "center" });

        doc.setFontSize(10);
        doc.text(`Generated: ${today}`, pageWidth - 10, 10, { align: "right" });

        // ✅ Source Name
        if (SourceNames && SourceNames.length > 0) {
            doc.text(`Source: ${SourceNames.join(", ")}`, 10, 16);
        }

        const startY = SourceNames?.length ? 22 : 20;

        // 🔹 FORMAT NUMBER
        const formatNumber = (num) => {
            if (!num) return "0";
            return Number(num).toLocaleString("en-IN");
        };

        const months = selectedMonths.map(m => m.value).sort((a, b) => a - b);

        const isMoreThan6 = months.length > 6;

        // =========================
        // ✅ CASE 1: ≤ 6 MONTHS
        // =========================
        if (!isMoreThan6) {

            const monthLabels = months.map(m => {
                const d = new Date(yearObj.value, m - 1);
                return d.toLocaleString('default', { month: 'short', year: 'numeric' });
            });

            const headerRow1 = [
                { content: "Property Name", rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
                ...monthLabels.map(label => ({
                    content: label,
                    colSpan: 3,
                    styles: { halign: 'center' }
                }))
            ];

            const headerRow2 = months.flatMap(() => ([
                { content: "RM NTS", styles: { halign: 'center', fillColor: [41, 128, 185] } },
                { content: "REV.", styles: { halign: 'center', fillColor: [41, 128, 185] } },
                { content: "ARR", styles: { halign: 'center', fillColor: [41, 128, 185] } }
            ]));

            const rows = data
                .filter(item => {
                    return months.some(m =>
                        Number(item[`OCC_${m}`] || 0) !== 0 ||
                        Number(item[`REV_${m}`] || 0) !== 0 ||
                        Number(item[`ARR_${m}`] || 0) !== 0
                    );
                })
                .map(item => [
                    item.PropertyName || "",
                    ...months.flatMap(m => ([
                        formatNumber(item[`OCC_${m}`] || 0),
                        formatNumber(item[`REV_${m}`] || 0),
                        formatNumber(item[`ARR_${m}`] || 0)
                    ]))
                ]);

            // 🔹 WIDTH FIX
            const totalCols = 1 + (months.length * 3);
            const totalWidth = pageWidth - 20;

            const firstColWidth = 40;
            const otherColWidth = (totalWidth - firstColWidth) / (totalCols - 1);

            const columnStyles = {
                0: { cellWidth: firstColWidth, halign: 'left', valign: 'middle' }
            };

            for (let i = 1; i < totalCols; i++) {
                columnStyles[i] = { cellWidth: otherColWidth, halign: 'right' };
            }

            autoTable(doc, {
                startY,
                head: [headerRow1, headerRow2],
                body: rows,
                theme: 'grid',
                styles: {
                    fontSize: 7,
                    cellPadding: 1.5,
                    lineWidth: 0.2
                },
                headStyles: {
                    fillColor: [41, 128, 185],
                    textColor: 255
                },
                columnStyles,
                margin: { left: 10, right: 10 }
            });
        }

        // =========================
        // 🔥 CASE 2: > 6 MONTHS
        // =========================
        else {

            const monthLabels = months.map(m => {
                const d = new Date(yearObj.value, m - 1);
                return d.toLocaleString('default', { month: 'short', year: 'numeric' });
            });

            const headerRow = [
                "Property Name",
                "Type",
                ...monthLabels
            ];

            const rows = [];

            data.forEach(item => {

                // 🔹 RM NTS
                rows.push([
                    {
                        content: item.PropertyName || "",
                        rowSpan: 2,
                        styles: { valign: 'middle', halign: 'left' }
                    },
                    { content: "RM NTS" },
                    ...months.map(m => formatNumber(item[`OCC_${m}`] || 0))
                ]);

                // 🔹 REV
                rows.push([
                    { content: "REV." },
                    ...months.map(m => formatNumber(item[`REV_${m}`] || 0))
                ]);

                // 🔹 ARR
                rows.push([
                    { content: "ARR" },
                    ...months.map(m => formatNumber(item[`ARR_${m}`] || 0))
                ]);
            });

            // 🔹 WIDTH FIX
            const totalCols = 2 + months.length;
            const totalWidth = pageWidth - 20;

            const firstColWidth = 40;
            const secondColWidth = 20;
            const otherColWidth = (totalWidth - firstColWidth - secondColWidth) / months.length;

            const columnStyles = {
                0: { cellWidth: firstColWidth, halign: 'left', valign: 'middle' },
                1: { cellWidth: secondColWidth, halign: 'left' }
            };

            for (let i = 2; i < totalCols; i++) {
                columnStyles[i] = { cellWidth: otherColWidth, halign: 'right' };
            }

            autoTable(doc, {
                startY,
                head: [headerRow],
                body: rows,
                theme: 'grid',
                styles: {
                    fontSize: 7,
                    cellPadding: 1.5,
                    lineWidth: 0.2,
                    valign: 'middle'
                },
                headStyles: {
                    fillColor: [41, 128, 185],
                    textColor: 255
                },
                columnStyles,
                margin: { left: 10, right: 10 }
            });
        }
    };

    // const handleReport = async (e) => {
    //     e.preventDefault();

    //     if (!selectedProperty.length || !selectedYear.length || !selectedMonth.length) {
    //         alert("Please select all fields");
    //         return;
    //     }

    //     // 🔥 SOURCE REPORT
    //     if (reportType === "source") {

    //         const PID = selectedProperty.map(p => p.value);
    //         const Codes = selectedSource.map(s => s.value);

    //         const doc = new jsPDF("landscape");
    //         const today = new Date().toLocaleDateString("en-GB", {
    //             day: "2-digit",
    //             month: "long",
    //             year: "numeric"
    //         });

    //         let isFirstPage = true;

    //         for (const yearObj of selectedYear) {

    //             const monthsArray = selectedMonth.map(m => m.value);

    //             let finalData = [];

    //             const sourceLoop = Codes.length ? Codes : [""];

    //             for (const sourceCode of sourceLoop) {

    //                 const payload = {
    //                     PID: `[${PID}]`,
    //                     Year: yearObj.value,
    //                     Month: `[${monthsArray}]`,
    //                     Code: sourceCode
    //                 };

    //                 const result = await dispatch(fetchBusinessMonthlyReport(payload));
    //                 const data = result.payload || [];

    //                 finalData = [...finalData, ...data];
    //             }

    //             const uniqueData = Object.values(
    //                 finalData.reduce((acc, item) => {
    //                     acc[item.BusinessSource] = item;
    //                     return acc;
    //                 }, {})
    //             );

    //             generateMultiMonthPage(
    //                 doc,
    //                 uniqueData,
    //                 selectedMonth,
    //                 yearObj,
    //                 today,
    //                 isFirstPage,
    //                 selectedProperty
    //             );

    //             isFirstPage = false;
    //         }

    //         doc.save("Source_Monthly_Report.pdf");
    //     }

    //     // 🔥 PROPERTY REPORT
    //     else {

    //         const PID = selectedProperty.map(p => p.value);
    //         const Codes = selectedSource.map(s => s.value);
    //         const SourceNames = selectedSource.map(s => s.label);

    //         const doc = new jsPDF("landscape");
    //         const today = new Date().toLocaleDateString("en-GB", {
    //             day: "2-digit",
    //             month: "long",
    //             year: "numeric"
    //         });

    //         let isFirstPage = true;

    //         for (const yearObj of selectedYear) {

    //             const monthsArray = selectedMonth.map(m => m.value);

    //             const payload = {
    //                 PID: `[${PID}]`,
    //                 Year: yearObj.value,
    //                 Month: `[${monthsArray}]`,
    //                 Code: Codes.length ? Codes[0] : ""
    //             };

    //             const result = await dispatch(fetchBusinessPropertyMonthlyReport(payload));
    //             const data = result.payload || [];

    //             generateMultiMonthPageProperty(   // 👈 naam change karo
    //                 doc,
    //                 data,
    //                 selectedMonth,
    //                 yearObj,
    //                 today,
    //                 isFirstPage,
    //                 SourceNames
    //             );

    //             isFirstPage = false;
    //         }

    //         doc.save("Property_Source_Monthly_Report.pdf");
    //     }
    // };

    const handleReport = async (e) => {
        e.preventDefault();

        if (!selectedProperty.length || !selectedYear.length || !selectedMonth.length) {
            alert("Please select all fields");
            return;
        }

        const PID = selectedProperty.map(p => p.value);
        const Codes = selectedSource.map(s => s.value);
        const SourceNames = selectedSource.map(s => s.label);

        const doc = new jsPDF("landscape");

        const today = new Date().toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "long",
            year: "numeric"
        });

        let isFirstPage = true;

        // =========================
        // 🔥 SOURCE REPORT
        // =========================
        if (reportType === "source") {

            for (const yearObj of selectedYear) {

                const monthsArray = selectedMonth.map(m => m.value);

                // ✅ NO LOOP - direct array pass
                const payload = {
                    PID: `[${PID}]`,
                    Year: yearObj.value,
                    Month: `[${monthsArray}]`,
                    Code: Codes.length ? `["${Codes.join('","')}"]` : ""
                };

                // console.log("Source payload", payload)

                const result = await dispatch(fetchBusinessMonthlyReport(payload));
                const finalData = result.payload || [];

                generateMultiMonthPage(
                    doc,
                    finalData,
                    selectedMonth,
                    yearObj,
                    today,
                    isFirstPage,
                    selectedProperty
                );

                isFirstPage = false;
            }

            doc.save("Source_Monthly_Report.pdf");
        }

        // =========================
        // 🔥 PROPERTY REPORT
        // =========================
        else {

            for (const yearObj of selectedYear) {

                const monthsArray = selectedMonth.map(m => m.value);

                // ✅ SAME FIX HERE
                const payload = {
                    PID: `[${PID}]`,
                    Year: yearObj.value,
                    Month: `[${monthsArray}]`,
                    Code: Codes.length ? `["${Codes.join('","')}"]` : ""
                };

                // console.log("Property payload", payload)

                const result = await dispatch(fetchBusinessPropertyMonthlyReport(payload));
                const data = result.payload || [];

                generateMultiMonthPageProperty(
                    doc,
                    data,
                    selectedMonth,
                    yearObj,
                    today,
                    isFirstPage,
                    SourceNames
                );

                isFirstPage = false;
            }

            doc.save("Property_Source_Monthly_Report.pdf");
        }
    };

    const handleClear = () => {
        setSelectedProperty([]);
        setSelectedYear([]);
        setSelectedMonth([]);
        setSelectedSource([]);
    };


    const buildExcelSheetData = (data, selectedMonths, yearObj, nameKey, nameLabel) => {
        const months = selectedMonths.map(m => m.value).sort((a, b) => a - b);

        const monthLabels = months.map(m => {
            const d = new Date(yearObj.value, m - 1);
            return d.toLocaleString('default', { month: 'short', year: 'numeric' });
        });

        // Row 1: Name col + month labels (each spanning 3 cols)
        const headerRow1 = [nameLabel, ...monthLabels.flatMap(label => [label, "", ""])];

        // Row 2: sub headers
        const headerRow2 = ["", ...months.flatMap(() => ["RM NTS", "REV.", "ARR"])];

        const rows = data
            .filter(item =>
                months.some(m =>
                    Number(item[`OCC_${m}`] || 0) !== 0 ||
                    Number(item[`REV_${m}`] || 0) !== 0 ||
                    Number(item[`ARR_${m}`] || 0) !== 0
                )
            )
            .map(item => [
                item[nameKey] || "",
                ...months.flatMap(m => [
                    Number(item[`OCC_${m}`] || 0),
                    Number(item[`REV_${m}`] || 0),
                    Number(item[`ARR_${m}`] || 0)
                ])
            ]);

        const aoa = [headerRow1, headerRow2, ...rows];

        // Merges: name column spans 2 header rows, each month label spans 3 columns
        const merges = [{ s: { r: 0, c: 0 }, e: { r: 1, c: 0 } }];

        months.forEach((_, idx) => {
            const startCol = 1 + idx * 3;
            merges.push({ s: { r: 0, c: startCol }, e: { r: 0, c: startCol + 2 } });
        });

        return { aoa, merges };
    };


    const handleExcelExport = async (e) => {
        e.preventDefault();

        if (!selectedProperty.length || !selectedYear.length || !selectedMonth.length) {
            alert("Please select all fields");
            return;
        }

        const PID = selectedProperty.map(p => p.value);
        const Codes = selectedSource.map(s => s.value);

        const wb = XLSX.utils.book_new();

        if (reportType === "source") {
            for (const yearObj of selectedYear) {
                const monthsArray = selectedMonth.map(m => m.value);

                const payload = {
                    PID: `[${PID}]`,
                    Year: yearObj.value,
                    Month: `[${monthsArray}]`,
                    Code: Codes.length ? `["${Codes.join('","')}"]` : ""
                };

                const result = await dispatch(fetchBusinessMonthlyReport(payload));
                const finalData = result.payload || [];

                const { aoa, merges } = buildExcelSheetData(
                    finalData, selectedMonth, yearObj, "BusinessSource", "Business Source"
                );

                const ws = XLSX.utils.aoa_to_sheet(aoa);
                ws['!merges'] = merges;
                XLSX.utils.book_append_sheet(wb, ws, `Source_${yearObj.value}`);
            }

            XLSX.writeFile(wb, "Source_Monthly_Report.xlsx");

        } else {
            for (const yearObj of selectedYear) {
                const monthsArray = selectedMonth.map(m => m.value);

                const payload = {
                    PID: `[${PID}]`,
                    Year: yearObj.value,
                    Month: `[${monthsArray}]`,
                    Code: Codes.length ? `["${Codes.join('","')}"]` : ""
                };

                const result = await dispatch(fetchBusinessPropertyMonthlyReport(payload));
                const data = result.payload || [];

                const { aoa, merges } = buildExcelSheetData(
                    data, selectedMonth, yearObj, "PropertyName", "Property Name"
                );

                const ws = XLSX.utils.aoa_to_sheet(aoa);
                ws['!merges'] = merges;
                XLSX.utils.book_append_sheet(wb, ws, `Property_${yearObj.value}`);
            }

            XLSX.writeFile(wb, "Property_Source_Monthly_Report.xlsx");
        }
    };

    return (
        <>
            {loading && (
                <div className="fullscreen-loader">
                    <div className="fas fa-spinner fa-spin fa-3x"></div>
                </div>
            )}

            <div className="wrapper">
                <ReservationSidebar />
                <div className="main-panel">
                    <ReservationHeader />

                    <div className="container analytics-report">
                        <div className="page-inner analytics-report__inner">
                            <div className="analytics-report__header">
                                <div>
                                    <h3>
                                        {reportType === "source"
                                            ? "Source wise Monthly Report"
                                            : "Property Source wise Monthly Report"}
                                    </h3>
                                    <p>Analyze performance metrics across multiple property sources and locations.</p>
                                </div>
                                
                            </div>

                            <form className="analytics-report__filters">
                                <div className="analytics-report__field analytics-report__field--full">
                                    <label>Selected Properties</label>
                                    <Select
                                        options={propertyOptions}
                                        isMulti
                                        value={selectedProperty}
                                        onChange={handlePropertyChange}
                                        placeholder="Select properties..."
                                        classNamePrefix="analytics-select"
                                        styles={reportSelectStyles}
                                    />
                                </div>

                                <div className="analytics-report__grid analytics-report__grid--monthly">
                                    <div className="analytics-report__field">
                                        <label>Year</label>
                                        <Select
                                            options={yearOptions}
                                            isMulti
                                            value={selectedYear}
                                            onChange={handleYearChange}
                                            placeholder="Select year..."
                                            classNamePrefix="analytics-select"
                                            styles={reportSelectStyles}
                                        />
                                    </div>

                                    <div className="analytics-report__field">
                                        <label>Month</label>
                                        <Select
                                            options={monthOptions}
                                            isMulti
                                            value={selectedMonth}
                                            onChange={handleMonthChange}
                                            placeholder="Select month..."
                                            classNamePrefix="analytics-select"
                                            styles={reportSelectStyles}
                                        />
                                    </div>

                                    <div className="analytics-report__field">
                                        <label>Source</label>
                                        <Select
                                            options={sourceOptions}
                                            isMulti
                                            value={selectedSource}
                                            onChange={handleSourceChange}
                                            placeholder="Select source..."
                                            classNamePrefix="analytics-select"
                                            styles={reportSelectStyles}
                                        />
                                    </div>

                                    <div className="analytics-report__field">
                                        <label>Report Type</label>
                                        <div className="analytics-report__radios">
                                            <label>
                                                <input
                                                    type='radio'
                                                    name="reportType"
                                                    checked={reportType === "source"}
                                                    onChange={() => setReportType("source")}
                                                />
                                                Source
                                            </label>
                                            <label>
                                                <input
                                                    type='radio'
                                                    name="reportType"
                                                    checked={reportType === "property"}
                                                    onChange={() => setReportType("property")}
                                                />
                                                Property
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className="analytics-report__actions">
                                    <button onClick={handleReport} className="analytics-report__btn analytics-report__btn--primary">
                                        <i className="fas fa-file-pdf"></i>
                                        Download PDF
                                    </button>

                                    <button className="analytics-report__btn analytics-report__btn--secondary" onClick={handleExcelExport}>
                                        <i className="fas fa-table"></i>
                                        Export Excel
                                    </button>

                                    <button type="button" className="analytics-report__btn analytics-report__btn--danger" onClick={handleClear}>
                                        <i className="fas fa-filter"></i>
                                        Clear Filter
                                    </button>
                                </div>
                            </form>

                            <div className="analytics-report__content">
                                <div className="analytics-report__empty">
                                    <div className="analytics-report__empty-icon">
                                        <i className="far fa-chart-bar"></i>
                                    </div>
                                    <h4>Ready to Generate</h4>
                                    <p>Adjust your filters and select the output format above to visualize the monthly distribution report.</p>
                                </div>
                             
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
